import { Auth } from 'aws-amplify';
import CryptoJS from 'crypto-js';
import { checkAuthState, signOut } from './APIUtils';
import { notificationManager } from './NotificationUtils';
import encryption, { ENVIRONMENT } from '../env/Configurations';

export const validateUserSession = async (history) => {
  try {
    const currUser = await checkAuthState();
    if (currUser) {
      const signInUserSession = currUser.signInUserSession;
      if (signInUserSession && !signInUserSession.isValid()) {
        handleSessionExpired(history);
      }
    } else {
      handleSessionExpired(history);
    }
  } catch (e) {
    console.error(e);
  }
};

let __sessionValidationIntervalId = null;
let __storageListener = null;

export const startSessionValidationJob = (history, intervalMs = 60000) => {
  try {
    __sessionExpiredFired = false;
    if (__sessionValidationIntervalId) {
      clearInterval(__sessionValidationIntervalId);
    }
    if (__storageListener) {
      window.removeEventListener('storage', __storageListener);
    }

    __storageListener = (e) => {
      if (e.key === 'user' && !e.newValue) {
        handleSessionExpired(history);
      }
    };

    window.addEventListener('storage', __storageListener);

    validateUserSession(history);
    __sessionValidationIntervalId = setInterval(() => {
      validateUserSession(history);
    }, intervalMs);
  } catch (e) {
    if (ENVIRONMENT === 'dev') {
      console.log('Failed to start session validation job', e);
    }
  }
};

export const stopSessionValidationJob = () => {
  try {
    if (__sessionValidationIntervalId) {
      clearInterval(__sessionValidationIntervalId);
      __sessionValidationIntervalId = null;
    }
    if (__storageListener) {
      window.removeEventListener('storage', __storageListener);
      __storageListener = null;
    }
  } catch (e) {
    if (ENVIRONMENT === 'dev') {
      console.log('Failed to stop session validation job', e);
    }
  }
};

export const emitSessionExpired = () => {
  try {
    const ev = new CustomEvent('session-expired');
    window.dispatchEvent(ev);
  } catch (e) {}
};

let __sessionExpiredFired = false;

export const handleSessionExpired = (history) => {
  if (__sessionExpiredFired) return;
  __sessionExpiredFired = true;
  try {
    signOut();
  } catch (e) {}
  try {
    stopSessionValidationJob && stopSessionValidationJob();
  } catch (e) {}
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('lang');
    for (var key in localStorage) {
      if (key.includes('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {}
  notificationManager('danger', 'Your session has expired');
  emitSessionExpired();
};

export const handleLogout = async (history, logoutClick = false, skipNavigation = false) => {
  signOut();
  localStorage.removeItem('user');
  localStorage.removeItem('lang');

  for (var key in localStorage) {
    if (key.includes('CognitoIdentityServiceProvider')) {
      localStorage.removeItem(key);
    }
  }

  if (skipNavigation) {
    if (logoutClick) {
      notificationManager('success', 'User logged out successfully');
    }
    try {
      stopSessionValidationJob && stopSessionValidationJob();
    } catch (e) {}
    return;
  }

  if (history) {
    if (logoutClick) {
      notificationManager('success', 'User logged out successfully');
    } else {
      notificationManager('danger', 'Your session has expired');
    }
    try {
      stopSessionValidationJob && stopSessionValidationJob();
    } catch (e) {}
    try {
      history.push('/prelogin');
    } catch (e) {
      window.location.href = '/prelogin';
    }
  } else {
    setTimeout(() => {
      window.location.href = '/prelogin';
    }, 800);
  }
};

export const getCurrentJwtToken = async () => {
  const currUser = await checkAuthState();
  if (currUser && currUser.signInUserSession && currUser.signInUserSession.isValid()) {
    const currSession = await Auth.currentSession();
    if (currSession && currSession.getIdToken()) {
      return currSession.getIdToken().getJwtToken();
    } else {
      handleLogout();
      return;
    }
  } else {
    handleLogout();
  }
};

export const encryptSessionStorage = (key = null, data = null) => {
  let encryptedData = null;

  try {
    if (encryption.ENCRYPTION_KEY !== null || getEncryptionKey() === true) {
      encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        encryption.ENCRYPTION_KEY,
      )?.toString();

      sessionStorage.setItem(key, encryptedData);
    }
  } catch (error) {
    // Due to multiple calls, sometimes the toast will not show, it's getting error, so I hide it.
    // notificationManager("danger", "Error when encrypting session storage.");
    if (ENVIRONMENT === 'dev') {
      console.log(error);
    }
  }
  return encryptedData;
};

export const decryptSessionStorage = (key = null) => {
  let decryptedData = null;

  try {
    if (encryption.ENCRYPTION_KEY !== null || getEncryptionKey() === true) {
      const encryptedText = sessionStorage.getItem(key);

      if (encryptedText) {
        const bytes = CryptoJS.AES.decrypt(encryptedText, encryption.ENCRYPTION_KEY);
        decryptedData = JSON.parse(bytes?.toString(CryptoJS.enc.Utf8));
      }
    }
  } catch (error) {
    // Due to multiple calls, sometimes the toast will not show, it's getting error, so I hide it.
    // notificationManager("error", "Error when decrypting session storage.");
    if (ENVIRONMENT === 'dev') {
      console.log(error);
    }
  }

  return decryptedData;
};

export const encryptLocalStorage = (key = null, data = null) => {
  let encryptedData = null;

  try {
    if (encryption.ENCRYPTION_KEY !== null || getEncryptionKey() === true) {
      encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        encryption.ENCRYPTION_KEY,
      )?.toString();

      localStorage.setItem(key, encryptedData);
    }
  } catch (error) {
    // Due to multiple calls, sometimes the toast will not show, it's getting error, so I hide it.
    // notificationManager("danger", "Error when encrypting local storage.");
    if (ENVIRONMENT === 'dev') {
      console.log(error);
    }
  }
  return encryptedData;
};

export const decryptLocalStorage = (key = null) => {
  let decryptedData = null;

  try {
    if (localStorage.getItem('lang') === 'en') {
      localStorage.clear();
      window.location.href = '/prelogin';
    }

    if (encryption.ENCRYPTION_KEY !== null || getEncryptionKey() === true) {
      const encryptedText = localStorage.getItem(key);

      if (encryptedText) {
        const bytes = CryptoJS.AES.decrypt(encryptedText, encryption.ENCRYPTION_KEY);
        decryptedData = JSON.parse(bytes?.toString(CryptoJS.enc.Utf8));
      }
    }
  } catch (error) {
    // Due to multiple calls, sometimes the toast will not show, it's getting error, so I hide it.
    // notificationManager("error", "Error when decrypting local storage.");
    if (ENVIRONMENT === 'dev') {
      console.log(error);
    }
  }

  return decryptedData;
};

export const getEncryptionKey = () => {
  try {
    if (encryption.ENCRYPTION_KEY === null) {
      const userData = Object.keys(localStorage)?.find((key) => key?.includes('userData'));

      if (!userData) {
        return false;
      }

      const loginId = JSON.parse(localStorage.getItem(userData))?.['Username'];
      if (!loginId) {
        return false;
      }

      encryption.ENCRYPTION_KEY = CryptoJS.MD5(loginId + 'CMS_ELEVATE')?.toString();
    }
  } catch (error) {
    // Due to multiple calls, sometimes the toast will not show, it's getting error, so I hide it.
    // notificationManager("error", "Error when getting encryption key.");
    if (ENVIRONMENT === 'dev') {
      console.log(error);
    }
  }

  return true;
};

export const getEncodedValue = (params = {}) => {
  let encodedObj = {};

  try {
    const encodeArrayAndObject = (value) => {
      let encodedValue = Array.isArray(value) ? [] : {};

      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string' || item === undefined || item === null) {
            if (item === undefined || item === null) {
              encodedValue.push(null);
            } else {
              encodedValue.push(encodeURIComponent(item));
            }
          } else {
            let encodedObj = {};
            for (const key of Object.keys(item)) {
              if (item[key]) {
                if (typeof item[key] === 'string') {
                  encodedObj[key] = encodeURIComponent(item[key]);
                } else if (Array.isArray(item[key])) {
                  encodedObj[key] = encodeArrayAndObject(item[key]);
                } else if (typeof item[key] === 'object') {
                  encodedObj[key] = encodeArrayAndObject(item[key]);
                } else {
                  encodedObj[key] = item[key];
                }
              } else {
                encodedObj[key] = item[key];
              }
            }
            encodedValue.push(encodedObj);
          }
        }
      } else {
        for (const key of Object.keys(value)) {
          if (value[key]) {
            if (typeof value[key] === 'string') {
              encodedValue[key] = encodeURIComponent(value[key]);
            } else if (Array.isArray(value[key])) {
              encodedValue[key] = encodeArrayAndObject(value[key]);
            } else if (typeof value[key] === 'object') {
              encodedValue[key] = encodeArrayAndObject(value[key]);
            } else {
              encodedValue[key] = value[key];
            }
          } else {
            encodedValue[key] = value[key];
          }
        }
      }

      return encodedValue;
    };

    for (const key of Object.keys(params)) {
      if (params[key]) {
        if (typeof params[key] === 'string') {
          encodedObj[key] = encodeURIComponent(params[key]);
        } else if (Array.isArray(params[key])) {
          encodedObj[key] = encodeArrayAndObject(params[key]);
        } else if (typeof params[key] === 'object') {
          encodedObj[key] = encodeArrayAndObject(params[key]);
        } else {
          encodedObj[key] = params[key];
        }
      } else {
        encodedObj[key] = params[key];
      }
    }
  } catch (exception) {
    notificationManager('error', 'Error when getting encoding values.');
  }

  return encodedObj;
};
